import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../utils/api';
import { ChevronDown, ChevronRight, ChevronLeft, Trophy, Flame, Footprints, TreePine, Droplets, Car, Award, Users, TrendingUp, Target, Zap, Star, Gift, BarChart3, Calendar, Clock, Hexagon, Medal, Crown, Gem, ShieldCheck } from 'lucide-react';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityImpactSections from '../components/community/CommunityImpactSections';

const COMM = `${API}/community`;
const TEAL_ACCENT = '#5DBFAD';
const TEAL_LIGHT = '#E8F5F3';
const ORANGE_ACCENT = '#F8AD7B';
const MISSED_COLOR = '#EF4444';

const CommunityImpactPage = () => {
  const { user, token } = useCommunityAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [scoreData, setScoreData] = useState(null);
  const [impactStats, setImpactStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('weekly');
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBadgesModal, setShowBadgesModal] = useState(false);

  const shouldShowLevels = searchParams.get('showLevels') === 'true';
  const [showImpactLevels, setShowImpactLevels] = useState(shouldShowLevels);
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);
  const [impactCarouselIndex, setImpactCarouselIndex] = useState(0);
  const [showAllAchievements, setShowAllAchievements] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [weekStreakData, setWeekStreakData] = useState(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);

  const getImpactBadgeIcon = (level) => {
    const config = { 'Bronze': { icon: Medal, color: '#CD7F32', bg: 'rgba(205,127,50,0.12)' }, 'Silver': { icon: Medal, color: '#A8A8A8', bg: 'rgba(168,168,168,0.12)' }, 'Gold': { icon: Medal, color: '#D4AF37', bg: 'rgba(212,175,55,0.12)' }, 'Platinum': { icon: Gem, color: '#9D5CFF', bg: 'rgba(157,92,255,0.12)' }, 'Hero': { icon: Trophy, color: '#F5A623', bg: 'rgba(245,166,35,0.12)' } }[level] || { icon: Star, color: TEAL_ACCENT, bg: TEAL_LIGHT };
    const I = config.icon;
    return <div style={{ width: 28, height: 28, borderRadius: 8, background: config.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><I size={16} color={config.color} strokeWidth={1.5} /></div>;
  };

  const getBadgeIcon = (type, size = 24) => {
    const icons = { 'first_action': { icon: Star, color: '#F5A623', bg: 'rgba(245,166,35,0.12)' }, 'streak_7': { icon: Flame, color: '#E8795E', bg: 'rgba(232,121,94,0.12)' }, 'helper': { icon: Users, color: TEAL_ACCENT, bg: TEAL_LIGHT }, 'leader': { icon: Crown, color: '#F5A623', bg: 'rgba(245,166,35,0.12)' }, 'default': { icon: Award, color: TEAL_ACCENT, bg: TEAL_LIGHT } };
    const badge = icons[type] || icons['default'];
    const I = badge.icon;
    return <div style={{ width: size + 16, height: size + 16, borderRadius: 12, background: badge.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><I size={size} color={badge.color} /></div>;
  };

  useEffect(() => { if (showStreakModal) fetchStreak(); }, [showStreakModal]);
  useEffect(() => { fetchData(); }, [leaderboardPeriod]);

  const fetchStreak = async () => {
    try { const r = await axios.get(`${COMM}/streak/week`, authHeaders); setWeekStreakData(r.data); } catch {}
  };

  const fetchData = async () => {
    try {
      const [scoreRes, impactRes, lbRes, bkRes] = await Promise.all([
        axios.get(`${COMM}/social-score/me`, authHeaders),
        axios.get(`${COMM}/impact/stats`, authHeaders),
        axios.get(`${COMM}/leaderboard`, authHeaders),
        axios.get(`${COMM}/social-score/breakdown`, authHeaders)
      ]);
      setScoreData(scoreRes.data);
      setImpactStats(impactRes.data);
      setLeaderboard(lbRes.data);
      setScoreBreakdown(bkRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const impactLevels = [
    { level: 'Bronze', icon: <Award size={18} color="#CD7F32" />, description: 'Getting started', range: '0 - 499' },
    { level: 'Silver', icon: <Star size={18} color="#A8A9AD" />, description: 'Building momentum', range: '500 - 1,499' },
    { level: 'Gold', icon: <Star size={18} color="#F28C28" fill="#F28C28" />, description: 'Consistent contributor', range: '1,500 - 4,999' },
    { level: 'Platinum', icon: <Gem size={18} color="#4DB6AC" />, description: 'Impact leader', range: '5,000 - 9,999' },
    { level: 'Hero', icon: <Trophy size={18} color="#E76F51" />, description: 'Top performer', range: '10,000+' },
  ];

  const getImpactTiles = () => {
    if (!impactStats) return [];
    return [
      { icon: Calendar, value: impactStats.events_joined || 0, label: 'Events Joined', description: 'Total events you participated in', borderColor: TEAL_ACCENT },
      { icon: Users, value: impactStats.orgs_connected || 0, label: 'Organisations', description: 'Connected with unique orgs', borderColor: '#8B5CF6' },
      { icon: Gift, value: impactStats.stars_redeemed || 0, label: 'Stars Redeemed', description: 'Stars used for rewards', borderColor: '#F59E0B' },
    ];
  };

  const impactTiles = getImpactTiles();

  if (loading || !scoreData) return <div className="loading">Loading...</div>;

  return (
    <div className="page-container">
      <CommunityTopHeader />
      <div className="content-wrapper" style={{ background: '#F8FAFB' }}>
        <div className="content-section" style={{ padding: '0 16px' }}>

          {/* Impact Level */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 24, background: 'rgba(77,182,172,0.06)', padding: 12, marginBottom: 16 }}>
            <div onClick={() => setShowImpactLevels(!showImpactLevels)} style={{ position: 'relative', zIndex: 1, background: '#FFFFFF', borderRadius: 16, padding: '20px 24px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 12, background: TEAL_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getImpactBadgeIcon(scoreData.impact_level)}</div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1F2937' }}>{scoreData.impact_level}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Tap to view all levels</div>
                  </div>
                </div>
                <ChevronDown size={24} color={TEAL_ACCENT} style={{ transform: showImpactLevels ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} />
              </div>
              {showImpactLevels && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Impact Levels</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {impactLevels.map(item => (
                      <div key={item.level} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: scoreData.impact_level === item.level ? TEAL_LIGHT : '#F9FAFB', border: scoreData.impact_level === item.level ? `1px solid ${TEAL_ACCENT}` : '1px solid transparent' }}>
                        <span style={{ fontSize: 20 }}>{item.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{item.level}</span>
                              {scoreData.impact_level === item.level && <span style={{ fontSize: 11, color: TEAL_ACCENT, fontWeight: 500 }}>Your Level</span>}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 500, color: scoreData.impact_level === item.level ? TEAL_ACCENT : '#9CA3AF' }}>{item.range} pts</span>
                          </div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>{item.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Score Breakdown */}
          {scoreBreakdown && (
            <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, background: 'rgba(76,175,142,0.05)', padding: 10, marginBottom: 16 }}>
              <button onClick={() => setShowScoreBreakdown(!showScoreBreakdown)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: showScoreBreakdown ? '16px 16px 0 0' : 16, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: TEAL_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BarChart3 size={20} color={TEAL_ACCENT} /></div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>Score Breakdown</div>
                    <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'left' }}>Current: <span style={{ fontWeight: 600, color: TEAL_ACCENT }}>{scoreData.social_score?.toLocaleString() || 0}</span> points</div>
                  </div>
                </div>
                <ChevronDown size={20} color="#9CA3AF" style={{ transform: showScoreBreakdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              </button>
              {showScoreBreakdown && (
                <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
                  {[
                    { label: 'Individual Activities', desc: 'Solo activities completed', count: scoreBreakdown.individual_count || 0, value: scoreBreakdown.activities_score || 0, color: TEAL_ACCENT, bgColor: TEAL_LIGHT },
                    { label: 'Team Activities', desc: 'Activities done with team members', count: scoreBreakdown.team_count || 0, value: scoreBreakdown.team_score || 0, color: '#8B5CF6', boost: '3x' },
                    { label: 'Company Activities', desc: 'Company-wide initiatives', count: scoreBreakdown.company_count || 0, value: scoreBreakdown.challenges_score || 0, color: '#F59E0B', boost: '5x' },
                    { label: 'Achievements', desc: 'Badges and milestones earned', count: scoreData.achievements?.length || 0, value: scoreBreakdown.achievement_score || 0, color: '#EF4444' }
                  ].map((item, i, arr) => {
                    const total = scoreData.social_score || 1;
                    const pct = Math.round((item.value / total) * 100);
                    return (
                      <div key={i} style={{ padding: '16px 20px', borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{item.label}</span>
                            <span style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: 10 }}>{item.count}</span>
                            {item.boost && <span style={{ fontSize: 10, fontWeight: 700, color: '#FFF', background: item.color, padding: '3px 8px', borderRadius: 10 }}>{item.boost} boost</span>}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>{item.desc}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ flex: 1, height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: item.color, borderRadius: 4 }} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, minWidth: 80, justifyContent: 'flex-end' }}>
                            <span style={{ fontSize: 16, fontWeight: 700, color: item.color }}>{item.value.toLocaleString()}</span>
                            <span style={{ fontSize: 12, color: '#9CA3AF' }}>({pct}%)</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Your Impact / Your Stats / Leaderboard + Streak & Badges modals ── */}
          <CommunityImpactSections />

          {/* Achievements */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, background: 'rgba(242,140,40,0.05)', padding: 12, marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', margin: 0 }}>Achievements</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {(scoreData.achievements?.length > 0 ? scoreData.achievements : []).slice(0, showAllAchievements ? undefined : 4).map(a => (
                <div key={a.id} style={{ background: '#FFFFFF', borderRadius: 12, padding: 12, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1.5px solid rgba(242,140,40,0.30)' }}>
                  <div style={{ marginBottom: 6 }}>{getBadgeIcon(a.badge_type || 'default', 20)}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#374151', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{a.title}</div>
                </div>
              ))}
              {(scoreData.achievements?.length || 0) === 0 && [...Array(4)].map((_, i) => (
                <div key={`locked-${i}`} style={{ background: '#F9FAFB', borderRadius: 12, padding: 12, textAlign: 'center', border: '1px dashed #D1D5DB', opacity: 0.6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#E5E7EB', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Award size={18} color="#9CA3AF" /></div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#9CA3AF' }}>Locked</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
      <CommunityBottomNav active="impact" />
    </div>
  );
};

export default CommunityImpactPage;
